import React from "react";
import { Link, useParams } from "react-router-dom";
import {
    Box,
    Typography,
    CircularProgress,
    Paper,
    Grid,
    Chip,
    Divider,
    Card,
    CardContent,
    List,
    ListItem,
    ListItemText,
    CardHeader,
    TableHead,
    Table,
    TableBody,
    TableCell,
    TableRow,
    TableContainer
} from "@mui/material";
import { Icon } from "@iconify/react";
import { makeStyles } from "@mui/styles";

const useStyles = makeStyles((theme) => ({
    tableStyle: {
        "& td": {
            padding: '0.4rem',
            verticalAlign: 'top'
        },
        "& th": {
            padding: '0.4rem'
        },
    },
    passbadge: {
        height: "24px",
        minWidth: "35px",
        lineHeight: "0",
        borderRadius: "6px",
        cursor: "default",
        alignItems: "center",
        whiteSpace: "nowrap",
        display: "inline-flex",
        justifyContent: "center",
        textTransform: "capitalize",
        padding: "0px 8px",
        fontSize: "0.75rem",
        fontWeight: "700",
        color: "rgb(14 112 4)",
        backgroundColor: "rgba(84, 214, 44, 0.16)"
    },
    warningbadge: {
        height: "24px",
        minWidth: "35px",
        lineHeight: "0",
        borderRadius: "6px",
        cursor: "default",
        alignItems: "center",
        whiteSpace: "nowrap",
        display: "inline-flex",
        justifyContent: "center",
        textTransform: "capitalize",
        padding: "0px 8px",
        fontSize: "0.75rem",
        fontWeight: "700",
        color: "rgb(84 52 12)",
        backgroundColor: "rgb(214 142 20 / 50%)"
    },
    failbadge: {
        height: "24px",
        minWidth: "35px",
        lineHeight: "0",
        borderRadius: "6px",
        cursor: "default",
        alignItems: "center",
        whiteSpace: "nowrap",
        display: "inline-flex",
        justifyContent: "center",
        textTransform: "capitalize",
        padding: "0px 8px",
        fontSize: "0.75rem",
        fontWeight: "700",
        color: "#b72136",
        backgroundColor: "#ff484229"
    }
}));

function DNSReport({ data, pageTitle }) {

    console.log('dnsreport=-=-', data);
    const classes = useStyles();
    let classMap = {
        'A+': classes.passbadge,
        'B': classes.passbadge,
        'C': classes.warningbadge,
        'D': classes.warningbadge,
        'E': classes.failbadge,
        'F': classes.failbadge
    };
    return (
        <Paper elevation={3} sx={{ padding: 3, width: "100%", maxWidth: 900, margin: '0 auto' }}>
            {/* Target Website */}
            <Typography variant="h4" fontWeight="bold" mb={3} color="rgb(0, 145, 70)" sx={{ textAlign: 'center' }}>
                {pageTitle}
            </Typography>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6" fontWeight="bold">
                    <Icon icon="mdi:web" width={24} height={24} style={{ marginRight: 8 }} />
                    Target:
                </Typography>
                <Chip label={data.target} color="primary" variant="outlined" sx={{ fontSize: "1rem", padding: "5px" }} />
            </Box>
            <Divider sx={{ mb: 2 }} />

            {/* Grades */}
            <Card elevation={2} sx={{ mb: 3, background: "#e8f5e9" }}>
                <CardContent>
                    <Typography variant="h6" fontWeight="bold">
                        <Icon icon="mdi:shield-check" width={24} height={24} style={{ marginRight: 8 }} />
                        Grades:
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemText
                                primary="DNS Grade"
                                secondary={<Chip label={data?.grades?.dnsGrade} color="success" variant="outlined" />}
                            />
                        </ListItem>
                        {data.grades?.reason?.map((reason, index) => (
                            <ListItem key={index}>
                                <ListItemText primary={`⚠️ ${reason}`} />
                            </ListItem>
                        ))}
                    </List>
                </CardContent>
            </Card>

            {/* Report Type */}
            <Box mb={2}>
                <Typography variant="h6" fontWeight="bold">
                    <Icon icon="mdi:file-document-box" width={24} height={24} style={{ marginRight: 8 }} />
                    Report Type:
                </Typography>
                <Typography variant="body1">{data.reportType}</Typography>
            </Box>

            {/* Results Section */}
            <Card elevation={2} sx={{ mb: 3, background: "#f1f8ff" }}>
                <CardContent>
                    <Typography variant="h6" fontWeight="bold">
                        <Icon icon="mdi:check-circle-outline" width={24} height={24} style={{ marginRight: 8 }} />
                        Scan Results:
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Typography fontWeight="bold">Latency:</Typography>
                            <Typography>{data.result?.latency}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography fontWeight="bold">TCP Support:</Typography>
                            <Chip label={data.result?.tcpSupport ? "Yes" : "No"} color={data.result?.tcpSupport ? "success" : "error"} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography fontWeight="bold">Recursive Query Allowed:</Typography>
                            <Chip label={data.result?.recursiveQueryAllowed ? "Yes" : "No"} color={data.result?.recursiveQueryAllowed ? "warning" : "error"} />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Name Servers */}
            <Card elevation={2} sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" fontWeight="bold">
                        <Icon icon="mdi:server-network" width={24} height={24} style={{ marginRight: 8 }} />
                        Name Servers:
                    </Typography>
                    <List dense>
                        {data.result?.nameservers.map((server, index) => (
                            <ListItem key={index}>
                                <ListItemText primary={`🖥 ${server}`} />
                            </ListItem>
                        ))}
                    </List>
                </CardContent>
            </Card>

            {/* MX Records */}
            <Card elevation={2} sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" fontWeight="bold">
                        <Icon icon="mdi:email-multiple" width={24} height={24} style={{ marginRight: 8 }} />
                        Mail Exchange (MX) Records:
                    </Typography>
                    <List dense>
                        {data.result?.mx.map((mxRecord, index) => (
                            <ListItem key={index}>
                                <ListItemText primary={`📩 ${mxRecord}`} />
                            </ListItem>
                        ))}
                    </List>
                </CardContent>
            </Card>

            {/* PTR Records */}
            <Card elevation={2} sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" fontWeight="bold">
                        <Icon icon="mdi:map-marker" width={24} height={24} style={{ marginRight: 8 }} />
                        PTR Records:
                    </Typography>
                    <List dense>
                        {data.result?.ptr.map((ptrRecord, index) => (
                            <ListItem key={index}>
                                <ListItemText primary={`🔍 ${ptrRecord}`} />
                            </ListItem>
                        ))}
                    </List>
                </CardContent>
            </Card>

            {/* A Records */}
            <Card elevation={2} sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" fontWeight="bold">
                        <Icon icon="mdi:ip" width={24} height={24} style={{ marginRight: 8 }} />
                        A Records:
                    </Typography>
                    <List dense>
                        {data.result?.aRecords.map((aRecord, index) => (
                            <ListItem key={index}>
                                <ListItemText primary={`🌎 ${aRecord}`} />
                            </ListItem>
                        ))}
                    </List>
                </CardContent>
            </Card>

            {/* SRV Records */}
            
                <Card elevation={2} sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" fontWeight="bold">
                            <Icon icon="mdi:wrench-outline" width={24} height={24} style={{ marginRight: 8 }} />
                            SRV Records:
                        </Typography>
                        {data?.result?.srv?.length > 0 && ( <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Service</strong></TableCell>
                                        <TableCell><strong>Protocol</strong></TableCell>
                                        <TableCell><strong>Priority</strong></TableCell>
                                        <TableCell><strong>Port</strong></TableCell>
                                        <TableCell><strong>Target</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data?.result?.srv.map((srv, index) => (
                                        <TableRow key={index}>
                                            <TableCell>{srv.service}</TableCell>
                                            <TableCell>{srv.protocol}</TableCell>
                                            <TableCell>{srv.priority}</TableCell>
                                            <TableCell>{srv.port}</TableCell>
                                            <TableCell>{srv.target}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>)}
                    </CardContent>
                </Card>
            

            {/* CAA Records */}
            
                <Card elevation={2} sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" fontWeight="bold">
                            <Icon icon="mdi:certificate-outline" width={24} height={24} style={{ marginRight: 8 }} />
                            CAA Records:
                        </Typography>
                        {data?.result?.caa?.length > 0 && ( <List dense>
                            {data.result?.caa.map((record, index) => (
                                <ListItem key={index}>
                                    <ListItemText primary={`🔏 ${record.tag}: ${record.value}`} />
                                </ListItem>
                            ))}
                        </List> )}
                    </CardContent>
                </Card>
           
            {/* SOA Record */}
            <Card elevation={2} sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" fontWeight="bold">
                        <Icon icon="mdi:file-table-box" width={24} height={24} style={{ marginRight: 8 }} />
                        SOA Record:
                    </Typography>
                    {data.result?.soa ? (
                        <TableContainer component={Paper} sx={{ mt: 2 }}>
                            <Table>
                                <TableBody>
                                    <TableRow>
                                        <TableCell><strong>Primary Nameserver</strong></TableCell>
                                        <TableCell>{data.result?.soa.primaryNameserver}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell><strong>Admin Email</strong></TableCell>
                                        <TableCell>{data.result?.soa.adminEmail}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell><strong>Serial Number</strong></TableCell>
                                        <TableCell>{data.result?.soa.serialNumber}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell><strong>Refresh Interval</strong></TableCell>
                                        <TableCell>{data.result?.soa.refresh} seconds</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell><strong>Retry Interval</strong></TableCell>
                                        <TableCell>{data.result?.soa.retry} seconds</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell><strong>Expire Time</strong></TableCell>
                                        <TableCell>{data.result?.soa.expire} seconds</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell><strong>Minimum TTL</strong></TableCell>
                                        <TableCell>{data.result?.soa.minimumTTL} seconds</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Typography variant="body2" color="textSecondary">
                            No SOA record found.
                        </Typography>
                    )}
                </CardContent>
            </Card>

        </Paper>
    );
}


export default DNSReport;